import { DomesticContentAttestmentModalLabels } from '@/helpers/getDomesticContentAttestmentModalLabels';
import { isDomesticContentEffectivelyConfirmedForAccount } from '@/helpers/isDomesticContentEffectivelyConfirmedForAccount';
import { parseSystemDesignPayload } from '@/helpers/parseSystemDesignPayload';
import { useSetPvSystemInitialValues } from '@/helpers/setPvSystemInitialValues';
import { useUpdateAddersMutation } from '@/services/adders';
import { useCalculateAccountItcMutation } from '@/services/itc';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { useGetQuotesQuery } from '@/services/quotes';
import { useCreateSystemDesignMutation, useGetCurrentDesignQuery } from '@/services/systemDesign';
import { Origin } from '@/types/ProductionValueCheck';
import { Alert, Box, Button, RadioGroup, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import { useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  AdderType,
  DCQualificationResponse,
  DomesticContentAttestmentType,
  MilestoneType,
  MountingType,
  ProgramType,
  QueueType,
  QuoteStatus,
} from 'types';

import { useArePrecedingMilestonesCompleted } from '../../hooks/useArePrecedingMilestonesCompleted';
import { useGetAccountQuery } from '../../services/accounts';
import { useGetPVSystemSchema } from '../Account/PVSystem';
import { ITCCalculateButton } from '../AdminTasks/ITCCalculator/ITCCalculateButton';
import { ITCCalculatorResults } from '../AdminTasks/ITCCalculator/ITCCalculatorResults';
import { DomesticContentAttestmentModal } from '../DomesticContentValueConfirmation/DomesticContentAttestmentModal';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSectionContainer from '../Forms/FormSectionContainer';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { DomesticContentMountingHardwareSection } from '../SystemDesign/DomesticContentMountingHardwareSection';
import { EquipmentDetailsFormSection } from '../SystemDesign/EquipmentDetailsFormSection';

import { buildAttestationPayload, isConfirmedDowngrade } from './buildAttestationPayload';
import { buildStorageAdders } from './buildStorageAdders';

const attestationOptions = [
  { id: 'pvOnly', value: DomesticContentAttestmentType.pvOnly, label: 'Solar Only Domestic Content Eligible' },
  {
    id: 'pvAndStorage',
    value: DomesticContentAttestmentType.pvAndStorage,
    label: 'Solar + Storage Domestic Content Eligible',
  },
  { id: 'optOut', value: DomesticContentAttestmentType.optOut, label: 'Opted Out of Domestic Content ' },
];

const getRecommendedAttestationType = (result: DCQualificationResponse): DomesticContentAttestmentType => {
  if (result.pvQualified && result.batteryQualified) return DomesticContentAttestmentType.pvAndStorage;
  if (result.pvQualified) return DomesticContentAttestmentType.pvOnly;
  return DomesticContentAttestmentType.optOut;
};

export function ITCSystemDesign() {
  const { id: accountId } = useParams<{ id: any }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(accountId);
  const programType = account?.programType;
  const [saveAdders] = useUpdateAddersMutation();
  const origin = location?.state?.from || null;
  const action = location?.state?.action || null;
  const { data: currentSystemDesign, isLoading: isDesignLoading } = useGetCurrentDesignQuery(
    { id: accountId, programType },
    { skip: !programType },
  );
  const { data: quotes } = useGetQuotesQuery({ id: accountId, programType }, { skip: !programType });
  const { data: domesticContentQueue } = useGetQueueByAccountIdAndTypeQuery(
    { accountId, type: QueueType.domesticContent },
    { skip: !accountId },
  );
  // An account can hold an approved queue and a completed confirmation requirement while its stored
  // status still reads underConsideration. The server guard treats that as confirmed, so both the
  // warning and the acknowledgement below have to key on the same value or they will disagree.
  const isEffectivelyConfirmed = isDomesticContentEffectivelyConfirmedForAccount(account, domesticContentQueue);
  const activeQuote = quotes?.find((quote) => quote.status === QuoteStatus.active);
  const batteryAdder = activeQuote?.adders?.find(
    (adder: AdderType) => adder === AdderType.arbitrageBattery || adder === AdderType.backupBattery,
  );
  // Render the storage section whenever the active quote references a battery adder OR the persisted
  // system design already has storage. Without the second condition, stale storage values stay hidden
  // but still present in form state, and get re-POSTed on Calculate (FIN-ITC-FEOC stale storage bug).
  const hasBattery = Boolean(batteryAdder) || (currentSystemDesign?.storage?.length ?? 0) > 0;

  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const [calculateItc, { data: itcCalculationResult, reset }] = useCalculateAccountItcMutation();
  const isLoading = isDesignLoading || isAccountLoading;

  const initialValues = useSetPvSystemInitialValues(account || ({} as any));

  const pvSystemSchema = useGetPVSystemSchema();

  const [attestationModalConfirmationLabel, setAttestationModalConfirmationLabel] = useState('');
  const [attestationModalConfirmationDescription, setAttestationModalConfirmationDescription] = useState('');
  const [selectedAttestationType, setSelectedAttestationType] = useState<DomesticContentAttestmentType | null>(null);
  const [recommendedAttestationType, setRecommendedAttestationType] = useState<DomesticContentAttestmentType | null>(
    null,
  );

  const isDomesticContentEligible = account?.itcAdderQualifications?.domesticContentEligible;
  const showDc = isDomesticContentEligible;

  const canSubmitInstallPackage = useArePrecedingMilestonesCompleted(account?.milestones, MilestoneType.install);

  // Not the most robust method, but the simplest. If coming from the PV System tab, we are just saving, otherwise we are heading to the install package.
  const isHeadedToInstall = origin !== Origin.PV_SYSTEM_TAB;
  const canProceedToInstall =
    itcCalculationResult?.pvFeocQualified === true ||
    itcCalculationResult?.feocExceptionGranted === true ||
    itcCalculationResult?.equipmentExceptionGranted === true;

  const {
    isOpen: isAttestationModalOpen,
    handleClose: handleCloseAttestationModal,
    handleOpen: handleOpenAttestationModal,
  } = useOpenClose();

  const handleCalculateItc = useCallback(
    async (values: any, formikHelpers: any) => {
      try {
        const basePayload = parseSystemDesignPayload({ ...currentSystemDesign, ...values });
        const sentPayload = {
          accountId,
          systemDesign: {
            ...basePayload,
            mountingType: MountingType.roof,
            mountingManufacturer:
              basePayload.mountingManufacturer ||
              basePayload.mountingHardware?.[0]?.mountingHardwareModels?.[0]?.manufacturer ||
              '',
            enphaseInverterAttested:
              values.inverterManufacturer === 'Enphase' ? !!values.enphaseInverterAck : undefined,
            enphaseStorageAttested:
              values.storageManufacturer === 'Enphase' ? !!values.enphaseStorageAck : undefined,
          },
          programType: programType as ProgramType,
        };
        await pvSystemSchema.validate(sentPayload.systemDesign);
        await createSystemDesign(sentPayload).unwrap();
        // Mirror every valid storage row into accountAdders. `buildStorageAdders` returns
        // [] when the active quote has no battery adder type or no storage row has both
        // manufacturer and model — preserving the existing "don't write a typeless adder"
        // guard. The post-FIN-4557 `upsertAdders` group-replaces by type, so the N entries
        // wholesale replace any existing battery rows of the same type while leaving other
        // adder types (e.g. solarReadiness) untouched.
        const storageAdders = buildStorageAdders(sentPayload.systemDesign?.storage, batteryAdder);
        if (storageAdders.length > 0) {
          await saveAdders({
            accountId,
            adders: { adders: storageAdders },
          }).unwrap();
        }
        const updatedSystemDesign = sentPayload.systemDesign;
        const result = await calculateItc({ id: accountId, systemDesign: updatedSystemDesign }).unwrap();
        const recommendedAttestationType = getRecommendedAttestationType(result);
        setSelectedAttestationType(recommendedAttestationType);
        setRecommendedAttestationType(recommendedAttestationType);
        const { label, description } = DomesticContentAttestmentModalLabels[recommendedAttestationType];
        setAttestationModalConfirmationLabel(label);
        setAttestationModalConfirmationDescription(description);
        formikHelpers.resetForm({ values });
      } catch (e: any) {
        console.error('ITC calculation call failed:', e);
        toast.error(e?.data?.message || 'Failed to calculate ITC');
      }
    },
    [
      accountId,
      currentSystemDesign,
      programType,
      pvSystemSchema,
      createSystemDesign,
      calculateItc,
      saveAdders,
      batteryAdder,
    ],
  );

  const navigateToNext = useCallback(() => {
    let redirectTo = `/accounts/${accountId}/installation-package/create`;
    if (origin === Origin.PV_SYSTEM_TAB) {
      redirectTo = `/accounts/${accountId}/system-design`;
    }
    if (origin === Origin.INSTALL_PACKAGE && action) {
      redirectTo = `/accounts/${accountId}/installation-package/${action}`;
    }
    navigate(redirectTo, { replace: true });
  }, [accountId, action, navigate, origin]);

  const getCancelRoute = () => {
    if (origin === Origin.PV_SYSTEM_TAB) {
      return `/accounts/${accountId}/system-design`;
    }
    return `/accounts/${accountId}`;
  };

  const handleConfirmAttestation = useCallback(async () => {
    const attestationPayload = {
      accountId,
      systemDesign: buildAttestationPayload({
        currentSystemDesign,
        selectedAttestationType,
        currentEligibilityStatus: account?.domesticContentEligibilityStatus,
        isEffectivelyConfirmed,
      }),
      programType: programType as ProgramType,
    };
    await createSystemDesign(attestationPayload).unwrap();
    navigateToNext();
  }, [
    programType,
    accountId,
    createSystemDesign,
    currentSystemDesign,
    navigateToNext,
    selectedAttestationType,
    account?.domesticContentEligibilityStatus,
    isEffectivelyConfirmed,
  ]);

  const handleSaveOrNext = useCallback(async () => {
    const isChangingFromRecommended =
      recommendedAttestationType && selectedAttestationType !== recommendedAttestationType;

    const { label, description, warning } =
      DomesticContentAttestmentModalLabels[recommendedAttestationType || DomesticContentAttestmentType.optOut];
    if (isChangingFromRecommended) {
      setAttestationModalConfirmationLabel('Confirm Different Attestation Type');
      setAttestationModalConfirmationDescription(warning);
    } else {
      setAttestationModalConfirmationLabel(label);
      setAttestationModalConfirmationDescription(description);
    }

    // Opting out of a confirmed account removes a compliance status that has already been reviewed
    // and signed off. That is worth saying plainly regardless of whether the reviewer is following
    // the recommendation, since accepting a recommended opt-out is otherwise the quietest path.
    if (
      isConfirmedDowngrade(
        selectedAttestationType,
        account?.domesticContentEligibilityStatus,
        isEffectivelyConfirmed,
      )
    ) {
      setAttestationModalConfirmationLabel('Remove Confirmed Domestic Content?');
      setAttestationModalConfirmationDescription(
        'This account already has Domestic Content confirmed. Opting out will remove that confirmed status and cannot be undone from this screen — it will require a new attestation and another review. Continue only if this system genuinely does not qualify.',
      );
    }

    if (showDc) {
      handleOpenAttestationModal();
    } else {
      navigateToNext();
    }
  }, [
    handleOpenAttestationModal,
    navigateToNext,
    recommendedAttestationType,
    selectedAttestationType,
    showDc,
    account?.domesticContentEligibilityStatus,
    isEffectivelyConfirmed,
  ]);

  return (
    <>
      <Helmet>
        <title>ITC System Design</title>
      </Helmet>
      <MainContainer>
        <Box padding="lg" childGap="md">
          <PageHeader
            eyebrow={<Link to={`/accounts/${accountId}`}>{account?.primaryApplicantName}</Link>}
            title={showDc ? 'ITC + Domestic Content System Design' : 'ITC System Design'}
            description={
              'Before submission of the full Install Package, please complete the System Design and ITC Calculation to determine whether this project is eligible under current Palmetto LightReach ITC policy. This step must be completed before moving forward to the Install Package.'
            }
          />
          {account && !canSubmitInstallPackage && (
            <Alert
              variant="warning"
              hasIcon
              title="Notice to Proceed has not been issued"
              message="Notice to Proceed has to be issued before submitting the Install Package."
            />
          )}
          {!isLoading && account && (
            <Formik
              enableReinitialize
              initialValues={{
                ...initialValues,
                mountingHardware: initialValues.mountingHardware?.length
                  ? initialValues.mountingHardware
                  : [
                      { type: 'rail' },
                      { type: 'fastenerClamp' },
                      { type: 'fastenerMlpe' },
                      { type: 'fastenerSplice' },
                    ],
              }}
              validationSchema={pvSystemSchema}
              onSubmit={handleCalculateItc}
            >
              {() => (
                <EnhancedFormikForm noValidate>
                  <FormSectionContainer>
                    <EquipmentDetailsFormSection
                      isOptionalForNtp={false}
                      isLoading={isLoading}
                      programType={programType}
                      showOptimizerCheckbox
                      showEnphaseCheckboxes
                      showStorageOptions={hasBattery}
                      allowMultipleBatteries
                      equipmentException={account?.equipmentException}
                      orgAlias={account?.organizationId}
                      useSearchableModelSelect
                    />
                    <DomesticContentMountingHardwareSection
                      isOptionalForNtp={false}
                      isLoading={isLoading}
                      programType={ProgramType.solar}
                      allowDeleteMountingHardware={false}
                      hideMountingType
                      orgAlias={account?.organizationId}
                    />
                  </FormSectionContainer>
                  <Box childGap="md">
                    {itcCalculationResult && (
                      <>
                        <Box childGap="md" margin="lg 0 0 0">
                          <ErrorBoundary
                            fallback={
                              <Box style={{ fontStyle: 'italic' }}>There was an error displaying the results.</Box>
                            }
                          >
                            <Box direction="column" justifyContent="center" alignItems="center">
                              <ITCCalculatorResults
                                data={itcCalculationResult}
                                showDomesticContent={showDc}
                                reset={reset}
                              />
                            </Box>
                          </ErrorBoundary>
                        </Box>
                        {isDomesticContentEligible && (
                          <Box childGap="md">
                            <Box as="h3" fontWeight="medium" fontSize="md">
                              Domestic Content Attestation Type
                            </Box>
                            <RadioGroup
                              name="selectDcAttestationType"
                              value={selectedAttestationType || ''}
                              options={attestationOptions.map((option) => ({
                                ...option,
                                label: (
                                  <Box fontSize="md">
                                    {option.value === recommendedAttestationType ? (
                                      <Box direction="row">
                                        {option.label}&nbsp;
                                        <Box as="span" fontWeight="bold">
                                          (Recommended)
                                        </Box>
                                      </Box>
                                    ) : (
                                      option.label
                                    )}
                                  </Box>
                                ),
                              }))}
                              onChange={(event) =>
                                setSelectedAttestationType(event.target.value as DomesticContentAttestmentType)
                              }
                              size="lg"
                              isDisabled
                            />
                            {selectedAttestationType && (
                              <Box as="p" color="body-secondary" fontSize="sm">
                                {`Based on the output of the ITC Calculator, this Project's Install Package
                            will be submitted as ${
                              attestationOptions.find((option) => option.value === selectedAttestationType)?.label
                            }. If this does not align with your expected DC Evaluation, please confirm all
                              equipment listed above is accurate, and recalculate the system eligibility.`}
                              </Box>
                            )}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                  <Box
                    direction={{
                      base: 'column',
                      tablet: 'row',
                    }}
                    justifyContent={{
                      tablet: 'flex-end',
                    }}
                    padding="lg 0 0 0"
                    gap="sm"
                  >
                    <Button
                      as="a"
                      navigate={() => navigate(getCancelRoute())}
                      variant="secondary"
                      tone="neutral"
                      size="md"
                    >
                      Cancel
                    </Button>
                    <ITCCalculateButton size="md" variant="secondary">
                      Calculate
                    </ITCCalculateButton>
                    <Button
                      variant="primary"
                      size="md"
                      isDisabled={isHeadedToInstall && !canProceedToInstall}
                      onClick={handleSaveOrNext}
                    >
                      {origin === Origin.PV_SYSTEM_TAB ? 'Save' : 'Next'}
                    </Button>
                  </Box>
                </EnhancedFormikForm>
              )}
            </Formik>
          )}

          <DomesticContentAttestmentModal
            isOpen={isAttestationModalOpen}
            onDismiss={handleCloseAttestationModal}
            onAttestmentConfirmed={handleConfirmAttestation}
            confirmationLabel={attestationModalConfirmationLabel}
            confirmationDescription={attestationModalConfirmationDescription}
            isLoading={isCreatingSystemDesign}
          />
        </Box>
      </MainContainer>
    </>
  );
}
